import type * as React from 'react';
import type { IProjectFullOrSimpleJSON } from 'external-api-types';
import type { FieldObject } from '../../lib/FieldObject';
import type { AllSourcesAccess, Schema, ObjectSchemaDefinition, ObjectDefinition, ContentSource } from '../../types/Schema';
import type { FieldPath } from '../../types/Field';
import type { PageFieldModel, RootFieldModel } from '../../types/FieldModel';
import type { PublishProjectScope } from '../../types/Publish';
import type { AssetSource } from '../../types/AssetSource';
import { HYDRATE_STATE } from '../../store/consts';
import type { DocumentVersion } from '../../types/DocumentVersion';
import type { Viewport } from '../../types/transition-types';
import { RESET_STUDIO_ACTION } from '../consts';
import type { State as LogsState } from './logs';
import studioLogsReducer from './logs';
import type { State as CollaboratorsState } from './collaborators';
import studioCollaboratorsReducer from './collaborators';
import type { State as FrameState } from './frame';
import studioFrameReducer from './frame';
import type { State as SplitTestingState } from './split-testing';
import studioSplitTestingReducer from './split-testing';
import type { State as PublishState } from './publish';
import studioPublishReducer from './publish';
import type { State as PublishRequesterState } from './publish/publish-requester';
import studioPublishRequesterReducer from './publish/publish-requester';
import type { State as FieldsState } from './fields';
import studioFieldsReducer from './fields';
import type { State as RootObjectsState } from './rootObjects';
import studioRootObjectsReducer from './rootObjects';
import type { State as UiState } from './ui';
import studioUiReducer, { localStorePaths as uiLocalStorePaths } from './ui';
import type { State as SocketState } from './socket';
import studioSocketReducer from './socket';
import type { State as AssetsState } from './assets';
import studioAssetsReducer from './assets';
import studioFieldsValidationReducer from './fields/validation';
import type { State as LocaleState } from './locale';
import studioLocaleReducer, { localStorePaths as localeLocalStorePaths } from './locale';
import type { State as RepoBranchesState } from './branches';
import branchesReducer from './branches';
import type { State as PresetsState } from './presets';
import presetsReducer from './presets';
import type { State as CustomActionsState } from './custom-actions';
import customActionsReducer, { localStorePaths as customActionsLocalStorePaths } from './custom-actions';
import type { State as PreviewControlsState } from './preview-controls';
import previewControlsReducer from './preview-controls';
import type { State as ContentTableState } from './content-table';
import contentTableReducer, { localStorePaths as contentTableLocalStorePaths } from './content-table';
import type { State as FieldsValidationState } from './fields/types';
import type { State as HostingState } from './hosting';
import hostingReducer from './hosting';

export const LOADING_ERRORS = {
    NO_PROJECT_FOUND: 'NO_PROJECT_FOUND',
    NO_GITHUB: 'NO_GITHUB',
    NO_GITHUB_COLLABORATOR: 'NO_GITHUB_COLLABORATOR'
} as const;

export enum CUSTOM_DOMAIN_STATUS {
    VERIFIED = 'VERIFIED',
    NOT_VERIFIED = 'NOT_VERIFIED',
    FAILED = 'FAILED',
    IN_VERIFICATION = 'IN_VERIFICATION',
    PENDING_VERIFICATION = 'PENDING_VERIFICATION'
}

export enum CUSTOM_DOMAIN_VALIDATION_TYPES {
    ALREADY_USED = 'ALREADY_USED',
    ALREADY_USED_USER_PROJECT = 'ALREADY_USED_USER_PROJECT',
    ALREADY_USED_ORG_PROJECT = 'ALREADY_USED_ORG_PROJECT'
}

export type ServerError = {
    code?: number;
    message?: string;
};

export enum PublishStrategies {
    pullRequest = 'pullRequest',
    merge = 'merge'
}

export type SitemapBranch = Record<string, SitemapEntry>;

export type SitemapEntry = ObjectSchemaDefinition & {
    children: SitemapBranch;
    title: string;
    url: string;
    locale?: string;
} & (
        | {
              environment: string;
              source: string;
              objectId: string;
          }
        | {
              objectId?: string;
          }
    );

export type SiteMapNode = SitemapEntryBase | SitemapEntryNoCSI | SitemapEntryCSI;

interface SitemapEntryBase {
    children: SitemapBranch;
    title: string;
    url: string;
    locale?: string;
}

export interface SitemapEntryNoCSI extends SitemapEntryBase, ObjectSchemaDefinition {
    source: 'schema';
    environment: string;
    objectId: string;
}

export interface SitemapEntryCSI extends SitemapEntryBase, ObjectSchemaDefinition {
    objectId: string;
}

export type FieldsDetailEntry = ObjectDefinition & {
    relativeFieldPath: FieldPath;
    locale?: string;
    xpath?: string;
    documentVersion?: DocumentVersion;
};

export type SidebarView =
    | {
          type: 'fields';
          entries: FieldsDetailEntry[];
          activeFieldPath?: FieldPath;
      }
    | {
          type: 'cms';
      }
    | {
          type: 'code';
      }
    | {
          type: 'presets';
      }
    | {
          type: 'style';
      }
    | {
          type: 'customButtonView';
          index: number;
      }
    | {
          type: 'customButtonViewOnboard';
      }
    | {
          type: 'upgradePlanView';
          tierId: string;
      };

export enum CheckingPageAvailability {
    Container = 'container',
    ContainerRestart = 'container-restart',
    Ssg = 'ssg'
}

export type StudioContainerSettings = {
    hcrHandled: boolean;
    ssgName?: string;
    ssgVersion?: string;
    nodeVersion: string;
    viewports?: Viewport[];
};

export type StudioContainerProjectStatus = {
    hasConfig: boolean;
    nodeVersion: string;
} & (
    | {
          isCSIEnabled: false;
      }
    | {
          isCSIEnabled: true;
          contentSourceTypes: string[];
          contentSourceMeta: {
              srcType: string;
              srcProjectId: string;
              srcVersion: string;
              csiVersion: string;
              supportsScheduledActions: boolean;
              supportsDocumentVersions: boolean;
          }[];
      }
);

export type StateProject = IProjectFullOrSimpleJSON & { publishStrategies: PublishStrategies[]; publishScopes: PublishProjectScope[] };

const DEFAULT_STUDIO_CONTAINER_SETTINGS: StudioContainerSettings = {
    hcrHandled: true,
    nodeVersion: ''
};

interface State {
    highlightedField: FieldObject | null;
    sidebarView: SidebarView | null;
    projectId: string;
    project: StateProject | null;
    isAfterDeploy: boolean;

    // used by non git based cms by stackbit-api
    duplicatableModels: $TSFixMe[]; // tstodo
    legacyPageModels: $TSFixMe[]; // any as it's legacy for local dev only
    containerSettings: StudioContainerSettings;
    assetSources: AssetSource[];
    containerProjectStatus: StudioContainerProjectStatus | null;
    stackbitVersion: string | null;
    environment: string | null;
    isFetchingDeploymentAccess: boolean;
    isFetchingPageData: boolean;
    isFetchingSiteMap: boolean;
    isRegistrationOverlayVisible: boolean;
    hasDeploymentAccess: boolean;
    hasDeploymentConnection: boolean;
    Modal: React.ElementType | null;
    pageData: $TSFixMe | null; // todo
    restartErrorCount: number;
    schema: Schema | null;
    models: RootFieldModel[];
    pageModels: PageFieldModel[];
    contentSources: ContentSource[];
    sitemap: SitemapBranch;
    sitemapFlatten: SitemapEntry[];
    ssgRestartRequested: boolean;
    isContainerRestartNeeded: boolean;
    isContainerInitiated: boolean;
    checkingPageAvailable: CheckingPageAvailability | null;
    isCodeEditorAvailable: boolean;
    codeEditorQueuedLocation: $TSFixMe | null;
    cmsAccess?: AllSourcesAccess;
    isPolling: boolean;
    isCheckingHealth: boolean;
    serverErrors: $TSFixMe[];
    fieldsErrors: $TSFixMe[];
    loadError: (typeof LOADING_ERRORS)[keyof typeof LOADING_ERRORS] | null;
    initialProjectLoading: boolean;
    deployError: Error | null;
    isDeploying: boolean;

    isUpdatingCustomDomain: boolean;
    customDomainError: string | null;
    customDomainValidation: { type: CUSTOM_DOMAIN_VALIDATION_TYPES } | null;
    customDomainStatus: CUSTOM_DOMAIN_STATUS;

    collaborators: CollaboratorsState;
    logs: LogsState;
    frame: FrameState;
    splitTesting: SplitTestingState;
    publish: PublishState;
    publishRequester: PublishRequesterState;
    fields: FieldsState;
    rootObjects: RootObjectsState;
    ui: UiState;
    socket: SocketState;
    assets: AssetsState;
    fieldsValidation: FieldsValidationState;
    locale: LocaleState;
    isContainerSocketConnected: boolean;
    branches: RepoBranchesState;
    presets: PresetsState;
    customActions: CustomActionsState;
    previewControls: PreviewControlsState;
    contentTable: ContentTableState;
    hosting: HostingState;
}

const getDefaultState = (): State => ({
    /**
     * Project has been deployed once in current session
     */
    isAfterDeploy: false,

    /**
     * List of page models that can be duplicated.
     */
    duplicatableModels: [],

    legacyPageModels: [],

    /**
     * The version of the stackbit.yaml file of the project (ex. '0.4.0')
     */
    stackbitVersion: null,

    /**
     * Current environment
     */
    environment: null,

    /**
     * Currently highlighted field on the page
     */
    highlightedField: null,

    /**
     * Whether there's an active request for fetching deployment access.
     */
    isFetchingDeploymentAccess: false,

    /**
     * Whether there's an active request for fetching page data.
     *
     */
    isFetchingPageData: false,

    /**
     * Whether there's an active request for fetching sitemap data.
     *
     */
    isFetchingSiteMap: false,

    /**
     * Studio registration overlay is presented to the user
     *
     */
    isRegistrationOverlayVisible: false,

    hasDeploymentAccess: false,

    hasDeploymentConnection: false,

    /**
     * Modal portal component for displaying modals outside the render context
     */
    Modal: null,

    // @todo break out into separate properties.
    pageData: null,

    projectId: '',

    /**
     * An object representing the current project.
     */
    project: null,

    /**
     * Number of restarts from crashed state
     */
    restartErrorCount: 0,

    /**
     * The CMS schema for the current project.
     */
    schema: null,

    models: [],

    pageModels: [],

    contentSources: [],

    /**
     * The list of pages found in the sitemap.
     */
    sitemap: {},

    sitemapFlatten: [],

    /**
     * A boolean flag indicating if user clicked restart SSG.
     *
     * After user restarts, this property remains true until container returns
     * ssgRestartNeeded = false. Only then, this property can be set back to false.
     *
     * This is useful to hide the restart button after user clicks on it, and
     * until container notifies API and API notifies studio that restart is no
     * longer needed.
     */
    ssgRestartRequested: false,

    isContainerRestartNeeded: false,

    isContainerInitiated: false,

    /**
     * Flag which tells that app checking if page returns any response - means ssg finished building it
     * and it returns any sort of html - 200 or 5xx
     */
    checkingPageAvailable: null,

    /**
     * Flag indicating if code editor is available for actions.
     */
    isCodeEditorAvailable: false,

    /**
     * A code editor location update that is queued while the code editor is loading.
     */
    codeEditorQueuedLocation: null,

    sidebarView: null,

    isPolling: false,

    isCheckingHealth: false,

    serverErrors: [],

    fieldsErrors: [],

    loadError: null,

    initialProjectLoading: false,

    deployError: null,

    isDeploying: false,

    containerSettings: DEFAULT_STUDIO_CONTAINER_SETTINGS,

    containerProjectStatus: null,

    assetSources: [],

    isUpdatingCustomDomain: false,
    customDomainError: null,
    customDomainValidation: null,
    customDomainStatus: CUSTOM_DOMAIN_STATUS.NOT_VERIFIED,
    locale: studioLocaleReducer(undefined, null),
    isContainerSocketConnected: false,
    collaborators: studioCollaboratorsReducer(undefined, null),
    logs: studioLogsReducer(undefined, null),
    frame: studioFrameReducer(undefined, null),
    splitTesting: studioSplitTestingReducer(undefined, null),
    publish: studioPublishReducer(undefined, null),
    publishRequester: studioPublishRequesterReducer(undefined, null),
    fields: studioFieldsReducer(undefined, null),
    rootObjects: studioRootObjectsReducer(undefined, null),
    ui: studioUiReducer(undefined, null),
    socket: studioSocketReducer(undefined, null),
    assets: studioAssetsReducer(undefined, null),
    fieldsValidation: studioFieldsValidationReducer(undefined, null),
    branches: branchesReducer(undefined, null),
    presets: presetsReducer(undefined, null),
    customActions: customActionsReducer(undefined, null),
    previewControls: previewControlsReducer(undefined, null),
    contentTable: contentTableReducer(undefined, null),
    hosting: hostingReducer(undefined, null)
});

const studioReducer = (state = getDefaultState(), action: $TSFixMe): State => {
    switch (action.type) {
        case HYDRATE_STATE: {
            return {
                ...getDefaultState(),
                ...action.data.studio
            };
        }

        case 'STUDIO_LOAD_ERROR': {
            return {
                ...state,
                loadError: action.error
            };
        }

        case 'SET_STUDIO_SERVER_ERRORS': {
            return {
                ...state,
                serverErrors: action.data.serverErrors
            };
        }

        case 'SET_STUDIO_FIELDS_ERRORS': {
            return {
                ...state,
                fieldsErrors: action.data.fieldsErrors
            };
        }

        case 'STUDIO_SET_SCHEMA': {
            return {
                ...state,
                schema: action.data.schema,
                models: action.data.models,
                pageModels: action.data.pageModels,
                contentSources: action.data.contentSources
            };
        }

        case 'FETCH_PAGE_MODELS_FAILED': {
            return {
                ...state,
                isFetchingPageData: false
            };
        }

        case 'FETCH_PAGE_MODELS_STARTED': {
            return {
                ...state,
                isFetchingPageData: true
            };
        }

        case 'FETCH_PAGE_MODELS_SUCCEEDED': {
            const {
                stackbitVersion,
                duplicatableModels,
                pageData,
                containerProjectStatus,
                containerSettings = DEFAULT_STUDIO_CONTAINER_SETTINGS,
                legacyPageModels
            } = action.data || {};

            return {
                ...state,
                stackbitVersion,
                containerSettings,
                containerProjectStatus,
                duplicatableModels: duplicatableModels || [],
                isFetchingPageData: false,
                pageData: pageData || {},
                legacyPageModels
            };
        }

        case 'STUDIO_SET_ASSET_SOURCES': {
            return {
                ...state,
                assetSources: action.data.assetSources
            };
        }

        case 'FETCH_DEPLOYMENT_ACCESS_STARTED': {
            return {
                ...state,
                isFetchingDeploymentAccess: true
            };
        }

        case 'FETCH_DEPLOYMENT_ACCESS_SUCCEEDED': {
            const { hasDeploymentAccess, hasDeploymentConnection } = action.data;
            return {
                ...state,
                hasDeploymentAccess,
                hasDeploymentConnection,
                isFetchingDeploymentAccess: false
            };
        }

        case 'FETCH_DEPLOYMENT_ACCESS_FAILED': {
            return {
                ...state,
                isFetchingDeploymentAccess: false
            };
        }

        case 'FETCH_PROJECT_SUCCEEDED': {
            return {
                ...state,
                project: action.data.project
            };
        }

        case 'FETCH_SITE_MAP_FAILED': {
            return {
                ...state,
                isFetchingSiteMap: false
            };
        }

        case 'FETCH_SITE_MAP_STARTED': {
            return {
                ...state,
                isFetchingSiteMap: true
            };
        }

        case 'FETCH_SITE_MAP_SUCCEEDED': {
            const { sitemap, sitemapFlatten } = action.data || {};
            return {
                ...state,
                isFetchingSiteMap: false,
                sitemap: sitemap || {},
                sitemapFlatten
            };
        }

        case 'SET_REGISTRATION_OVERLAY_VISIBLE': {
            return {
                ...state,
                isRegistrationOverlayVisible: action.data.visible
            };
        }

        case 'INIT_STUDIO': {
            const { Modal, environment, projectId } = action.data;
            // in case project was setup on any previous pages (wizard/import)
            // studio has to be initiated with existing project
            const { project } = state;
            return {
                ...getDefaultState(),
                projectId,
                project,
                Modal,
                environment
            };
        }

        case RESET_STUDIO_ACTION: {
            return {
                ...getDefaultState(),
                projectId: state.projectId
            };
        }

        case 'STUDIO_SET_ENVIRONMENT': {
            const { environment } = action.data || {};
            return {
                ...state,
                environment
            };
        }

        case 'RESTART_SSG': {
            return {
                ...state,
                ssgRestartRequested: action.data.ssgRestartRequested ?? false
            };
        }

        case 'SET_CHECKING_PAGE_AVAILABLE': {
            return {
                ...state,
                checkingPageAvailable: action.data.checkingPageAvailable
            };
        }

        case 'SET_CODE_EDITOR_AVAILABLE': {
            return {
                ...state,
                isCodeEditorAvailable: action.data?.isAvailable,
                codeEditorQueuedLocation: null
            };
        }

        case 'QUEUE_CODE_EDITOR_LOCATION': {
            const { location } = action.data;
            return {
                ...state,
                codeEditorQueuedLocation: location
            };
        }

        case 'ERROR_RESTARTING_SSG': {
            return {
                ...state,
                ssgRestartRequested: false
            };
        }

        case 'INCREMENT_RESTART_ERROR_COUNT': {
            return {
                ...state,
                restartErrorCount: state.restartErrorCount + 1
            };
        }

        case 'SET_POLLING': {
            return {
                ...state,
                isPolling: action.data.isPolling,
                isCheckingHealth: action.data.isCheckingHealth ?? state.isCheckingHealth
            };
        }

        case 'SET_CMS_ACCESS': {
            return {
                ...state,
                cmsAccess: action.data?.cmsAccess ?? undefined
            };
        }

        case 'SET_HIGHLIGHTED_FIELD': {
            return {
                ...state,
                highlightedField: action.data
            };
        }

        case 'SET_SIDEBAR_VIEW': {
            return {
                ...state,
                sidebarView: action.data
            };
        }

        case 'STUDIO_SET_IS_AFTER_DEPLOY': {
            return {
                ...state,
                isAfterDeploy: true
            };
        }

        case 'SET_INITIAL_PROJECT_LOADING': {
            const { initialProjectLoading } = action.data || {};
            return {
                ...state,
                initialProjectLoading
            };
        }

        case 'DEPLOY_PROJECT_STARTED': {
            const { isDeploying, deployError } = action.data || {};
            return {
                ...state,
                deployError,
                isDeploying
            };
        }

        case 'DEPLOY_PROJECT_SUCCEEDED': {
            const { isDeploying, deployError } = action.data || {};
            return {
                ...state,
                deployError,
                isDeploying
            };
        }

        case 'DEPLOY_PROJECT_FAILED': {
            const { isDeploying, deployError } = action.data || {};
            return {
                ...state,
                deployError,
                isDeploying
            };
        }

        case 'UPDATE_CUSTOM_DOMAIN_STARTED': {
            return {
                ...state,
                isUpdatingCustomDomain: true,
                customDomainError: null
            };
        }

        case 'UPDATE_CUSTOM_DOMAIN_DONE': {
            const { validation } = action.data || {};
            return {
                ...state,
                isUpdatingCustomDomain: false,
                customDomainValidation: validation
            };
        }

        case 'UPDATE_CUSTOM_DOMAIN_FAILED': {
            const { error } = action || {};
            return {
                ...state,
                isUpdatingCustomDomain: false,
                customDomainError: error
            };
        }

        case 'VERIFY_CUSTOM_DOMAIN_STARTED': {
            const { status: customDomainStatus } = action.data || {};

            return {
                ...state,
                customDomainStatus,
                customDomainError: null
            };
        }

        case 'VERIFY_CUSTOM_DOMAIN_DONE': {
            const { status: customDomainStatus } = action.data || {};

            return {
                ...state,
                customDomainStatus
            };
        }

        case 'VERIFY_CUSTOM_DOMAIN_FAILED': {
            const { error } = action || {};

            return {
                ...state,
                customDomainStatus: CUSTOM_DOMAIN_STATUS.FAILED,
                customDomainError: error
            };
        }

        case 'CONTAINER_SOCKET_CONNECTED': {
            return {
                ...state,
                isContainerSocketConnected: true
            };
        }

        case 'CONTAINER_SOCKET_DISCONNECTED': {
            return {
                ...state,
                isContainerSocketConnected: false
            };
        }

        case 'STUDIO_SET_CONTAINER_RESTART_NEEDED': {
            return {
                ...state,
                isContainerRestartNeeded: action.data
            };
        }

        case 'STUDIO_SET_CONTAINER_INITIATED': {
            return {
                ...state,
                isContainerInitiated: action.data
            };
        }

        default: {
            return state;
        }
    }
};

export default (state = getDefaultState(), action: $TSFixMe): State => {
    const studioState = studioReducer(state, action);
    return {
        ...studioState,
        collaborators: studioCollaboratorsReducer(state.collaborators, action),
        logs: studioLogsReducer(state.logs, action),
        frame: studioFrameReducer(state.frame, action),
        splitTesting: studioSplitTestingReducer(state.splitTesting, action),
        publish: studioPublishReducer(state.publish, action),
        publishRequester: studioPublishRequesterReducer(state.publishRequester, action),
        fields: studioFieldsReducer(state.fields, action),
        rootObjects: studioRootObjectsReducer(state.rootObjects, action),
        ui: studioUiReducer(state.ui, action),
        socket: studioSocketReducer(state.socket, action),
        assets: studioAssetsReducer(state.assets, action),
        fieldsValidation: studioFieldsValidationReducer(state.fieldsValidation, action),
        branches: branchesReducer(state.branches, action),
        presets: presetsReducer(state.presets, action),
        customActions: customActionsReducer(state.customActions, action),
        previewControls: previewControlsReducer(state.previewControls, action),
        contentTable: contentTableReducer(state.contentTable, action),
        hosting: hostingReducer(state.hosting, action),
        locale: studioLocaleReducer(state.locale, action)
    };
};

export const localStorePaths = [...uiLocalStorePaths, ...localeLocalStorePaths, ...customActionsLocalStorePaths, ...contentTableLocalStorePaths, 'projectId'];
